import { useState } from 'react';
import { Link } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

type Status = 'in-progress' | 'planned' | 'completed';
type Category = 'Care' | 'Calendar' | 'Communication' | 'Platform' | 'Mobile' | 'Compliance';

interface RoadmapItem {
  id: string;
  title: string;
  description: string;
  status: Status;
  category: Category;
  tag?: string;
}

// ─── Roadmap data ─────────────────────────────────────────────────────────────

const ROADMAP_ITEMS: RoadmapItem[] = [
  // ── Completed ──────────────────────────────────────────────────────────────
  {
    id: 'multi-user-families',
    title: 'Multi-user family accounts',
    description:
      'Invite family members to join a shared care group with role-based permissions — Owner, Member, or Viewer.',
    status: 'completed',
    category: 'Platform',
  },
  {
    id: 'dependent-profiles',
    title: 'Dependent profiles',
    description:
      'Create detailed profiles for those in your care, including medical info, GP details, and emergency contacts.',
    status: 'completed',
    category: 'Care',
  },
  {
    id: 'task-management',
    title: 'Task management',
    description:
      'Assign care tasks to family members with due dates and recurring schedules. Track completion at a glance.',
    status: 'completed',
    category: 'Care',
  },
  {
    id: 'medication-tracking',
    title: 'Medication tracking',
    description:
      'Schedule medications with dose and timing, and log when they have been taken, missed, or skipped.',
    status: 'completed',
    category: 'Care',
  },
  {
    id: 'appointment-scheduling',
    title: 'Appointment scheduling',
    description:
      'Book and track medical appointments. Assign a family member as the attending carer and add location details.',
    status: 'completed',
    category: 'Calendar',
  },
  {
    id: 'calendar-view',
    title: 'Unified calendar',
    description:
      'A shared weekly view across tasks, medications, and appointments — a single picture of the care schedule.',
    status: 'completed',
    category: 'Calendar',
  },
  {
    id: 'family-messaging',
    title: 'Family messaging',
    description:
      'Real-time chat between family members, with direct messages and group threads for care coordination.',
    status: 'completed',
    category: 'Communication',
  },
  {
    id: 'approval-workflows',
    title: 'Guardian approval workflows',
    description:
      'Require sign-off from co-guardians before confirming shared appointments or activities.',
    status: 'completed',
    category: 'Care',
  },
  // ── In Progress ───────────────────────────────────────────────────────────
  {
    id: 'multi-dependent-tagging',
    title: 'Multi-dependent support',
    description:
      'Tag tasks, appointments, and activities across multiple dependents in the same family simultaneously.',
    status: 'in-progress',
    category: 'Care',
    tag: 'In development',
  },
  {
    id: 'notification-centre',
    title: 'Notification centre',
    description:
      'A dedicated inbox for approval requests, activity updates, and care reminders — with full searchable history.',
    status: 'in-progress',
    category: 'Communication',
    tag: 'In development',
  },
  // ── Planned ───────────────────────────────────────────────────────────────
  {
    id: 'push-notifications',
    title: 'Push notifications',
    description:
      'Receive real-time alerts on your phone for medication reminders, task assignments, and care decisions.',
    status: 'planned',
    category: 'Mobile',
  },
  {
    id: 'email-notifications',
    title: 'Email notifications',
    description:
      'Daily or weekly care digests and urgent alerts delivered straight to your inbox.',
    status: 'planned',
    category: 'Communication',
  },
  {
    id: 'mobile-apps',
    title: 'iOS & Android apps',
    description:
      'Native mobile apps for faster access, offline support, and a best-in-class care experience on your phone.',
    status: 'planned',
    category: 'Mobile',
  },
  {
    id: 'data-export',
    title: 'Data export & GDPR controls',
    description:
      "Download a full copy of your family's care records at any time, and permanently delete your account.",
    status: 'planned',
    category: 'Compliance',
  },
  {
    id: 'subscription-plans',
    title: 'Subscription plans',
    description:
      'Flexible pricing for families of all sizes, with a generous free tier that never expires.',
    status: 'planned',
    category: 'Platform',
  },
  {
    id: 'care-diary',
    title: 'Care diary',
    description:
      'A running log of care events, notes, and observations shared across the family — a single source of truth.',
    status: 'planned',
    category: 'Care',
  },
];

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<Status, {
  label: string;
  dotClass: string;
  badgeClass: string;
  borderClass: string;
  sectionClass: string;
}> = {
  'in-progress': {
    label: 'In Progress',
    dotClass: 'bg-amber-400',
    badgeClass: 'bg-amber-100 text-amber-700',
    borderClass: 'border-l-amber-400',
    sectionClass: 'text-amber-700',
  },
  'planned': {
    label: 'Planned',
    dotClass: 'bg-blue-400',
    badgeClass: 'bg-blue-100 text-blue-700',
    borderClass: 'border-l-blue-400',
    sectionClass: 'text-blue-700',
  },
  'completed': {
    label: 'Completed',
    dotClass: 'bg-primary-500',
    badgeClass: 'bg-primary-100 text-primary-700',
    borderClass: 'border-l-primary-400',
    sectionClass: 'text-primary-700',
  },
};

const CATEGORY_ICONS: Record<Category, React.ReactNode> = {
  Care: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  Calendar: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  ),
  Communication: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
    </svg>
  ),
  Platform: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  ),
  Mobile: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
    </svg>
  ),
  Compliance: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  ),
};

const CATEGORY_COLORS: Record<Category, string> = {
  Care: 'text-accent-600 bg-accent-50',
  Calendar: 'text-primary-600 bg-primary-50',
  Communication: 'text-blue-600 bg-blue-50',
  Platform: 'text-purple-600 bg-purple-50',
  Mobile: 'text-indigo-600 bg-indigo-50',
  Compliance: 'text-gray-600 bg-gray-100',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function RoadmapCard({ item }: { item: RoadmapItem }) {
  const cfg = STATUS_CONFIG[item.status];
  return (
    <div className={`bg-white rounded-xl border border-gray-200 border-l-4 ${cfg.borderClass} p-5 flex flex-col gap-3 hover:shadow-sm transition-shadow`}>
      <div className="flex items-start justify-between gap-2">
        <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-md ${CATEGORY_COLORS[item.category]}`}>
          {CATEGORY_ICONS[item.category]}
          {item.category}
        </div>
        {item.tag && (
          <span className="text-[11px] font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
            {item.tag}
          </span>
        )}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{item.title}</h3>
        <p className="text-sm text-gray-500 mt-1 leading-relaxed">{item.description}</p>
      </div>
      <div className="mt-auto pt-1">
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${cfg.badgeClass}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dotClass}`} />
          {cfg.label}
        </span>
      </div>
    </div>
  );
}

function SectionHeader({ status, count }: { status: Status; count: number }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <div className="flex items-center gap-3 mb-5">
      <span className={`w-2.5 h-2.5 rounded-full ${cfg.dotClass}`} />
      <h2 className={`text-base font-semibold ${cfg.sectionClass}`}>{cfg.label}</h2>
      <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{count}</span>
    </div>
  );
}

// ─── RoadmapPage ──────────────────────────────────────────────────────────────

type FilterValue = 'all' | Status;

const FILTER_LABELS: Record<FilterValue, string> = {
  all: 'All',
  'in-progress': 'In Progress',
  planned: 'Planned',
  completed: 'Completed',
};

const ORDERED_STATUSES: Status[] = ['in-progress', 'planned', 'completed'];

export function RoadmapPage() {
  const [filter, setFilter] = useState<FilterValue>('all');

  const completedCount = ROADMAP_ITEMS.filter((i) => i.status === 'completed').length;
  const inProgressCount = ROADMAP_ITEMS.filter((i) => i.status === 'in-progress').length;
  const plannedCount = ROADMAP_ITEMS.filter((i) => i.status === 'planned').length;

  const visibleStatuses = filter === 'all'
    ? ORDERED_STATUSES
    : [filter as Status];

  return (
    <div className="min-h-screen bg-cream-100 flex flex-col">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-cream-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-fraunces text-2xl font-light tracking-tight text-navy-mid">
                <em className="font-medium text-primary-500" style={{ fontStyle: 'italic' }}>Kin</em>Care
              </span>
              <span className="hidden sm:inline text-sm text-gray-500">Care together.</span>
            </Link>
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                Features
              </Link>
              <Link to="/#how-it-works" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                How It Works
              </Link>
              <Link to="/pricing" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                Pricing
              </Link>
              <Link to="/roadmap" className="text-sm font-medium text-primary-700 transition-colors">
                Roadmap
              </Link>
            </nav>
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                Sign In
              </Link>
              <Link to="/signup" className="btn btn-accent text-sm">
                Get Started
              </Link>
            </div>
            {/* Mobile: just show Sign In */}
            <Link to="/login" className="md:hidden text-sm text-gray-600 hover:text-gray-900">
              Sign In
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* ── Hero ────────────────────────────────────────────────────────── */}
        <section className="py-14 sm:py-20 bg-white border-b border-cream-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary-600 mb-4">
                Product Roadmap
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                Building KinCare,{' '}
                <span className="text-primary-600">in the open.</span>
              </h1>
              <p className="mt-4 text-lg text-gray-600 leading-relaxed">
                See exactly what we're working on, what's coming next, and what we've already shipped.
                We believe families deserve to know what's being built for them.
              </p>

              {/* Stats */}
              <div className="mt-8 flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-primary-500" />
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold text-gray-900">{completedCount}</span> shipped
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold text-gray-900">{inProgressCount}</span> in progress
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-400" />
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold text-gray-900">{plannedCount}</span> planned
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Filter + Items ───────────────────────────────────────────────── */}
        <section className="py-10 sm:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Filter pills */}
            <div className="flex flex-wrap gap-2 mb-10">
              {(Object.keys(FILTER_LABELS) as FilterValue[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`text-sm px-4 py-1.5 rounded-full font-medium transition-colors border ${
                    filter === f
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400 hover:text-gray-900'
                  }`}
                >
                  {FILTER_LABELS[f]}
                </button>
              ))}
            </div>

            {/* Status sections */}
            <div className="space-y-12">
              {visibleStatuses.map((status) => {
                const items = ROADMAP_ITEMS.filter((i) => i.status === status);
                if (items.length === 0) return null;
                return (
                  <div key={status}>
                    <SectionHeader status={status} count={items.length} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {items.map((item) => (
                        <RoadmapCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Feedback CTA */}
            <div className="mt-16 bg-white rounded-2xl border border-cream-200 p-8 sm:p-10 text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Something missing?
              </h2>
              <p className="text-gray-600 text-sm mb-5 max-w-md mx-auto">
                We'd love to hear what would make KinCare more useful for your family.
                Get in touch and help shape what we build next.
              </p>
              <a
                href="mailto:hello@kin.care?subject=Feature request"
                className="btn btn-primary text-sm"
              >
                Share your ideas
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-gray-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <span className="font-fraunces text-xl font-light tracking-tight text-white">
                <em className="font-medium text-primary-300" style={{ fontStyle: 'italic' }}>Kin</em>Care
              </span>
              <p className="mt-2 text-sm">Care together.</p>
              <p className="mt-4 text-xs text-gray-500">
                &copy; {new Date().getFullYear()} KinCare. All rights reserved.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/roadmap" className="hover:text-white transition-colors">Roadmap</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Sign In</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/cookies" className="hover:text-white transition-colors">Cookie Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Connect</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:hello@kin.care" className="hover:text-white transition-colors">
                    Contact Us
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
